import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";
import errMsg from "../../services/errMsg";

export const fetchAuditLogs = createAsyncThunk(
  "auditLog/fetch",
  async (params = {}, { rejectWithValue }) => {
    try {
      // `append` is a client-only flag handled by the reducer; strip from API params
      const { append, ...apiParams } = params;
      const { data } = await api.get(`/audit-logs`, { params: apiParams });
      return data.data;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to load audit logs"));
    }
  }
);

export const fetchAuditLog = createAsyncThunk(
  "auditLog/fetchOne",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/audit-logs/${id}`);
      return data.data.log;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to load entry"));
    }
  }
);

export const fetchAuditFacets = createAsyncThunk(
  "auditLog/facets",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/audit-logs/facets`);
      return data.data;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to load facets"));
    }
  }
);

// Default the From/To date filters to today so the page opens scoped to
// today's activity. The user can still widen the range from the filter UI.
const todayISO = () => new Date().toISOString().slice(0, 10);

const initialState = {
  items: [],
  pagination: { page: 1, limit: 25, total: 0, pages: 0 },
  query: {
    page: 1,
    limit: 25,
    search: "",
    customer: "",
    user: "",
    imei: "",
    actionType: "",
    entityType: "",
    severity: "",
    outcome: "",
    startDate: todayISO(),
    endDate: todayISO(),
    companyId: "",
    actorId: "",
    targetUserId: "",
    deviceId: "",
  },
  current: null,
  loading: false,
  loadingCurrent: false,
  facets: { actionTypes: [], entityTypes: [], severities: [], outcomes: [] },
  error: null,
};

const slice = createSlice({
  name: "auditLog",
  initialState,
  reducers: {
    setQuery(state, { payload }) {
      state.query = { ...state.query, ...payload };
    },
    clearCurrent(state) {
      state.current = null;
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchAuditLogs.pending, (s) => {
      s.loading = true;
      s.error = null;
    })
      .addCase(fetchAuditLogs.fulfilled, (s, a) => {
        s.loading = false;
        if (a.meta.arg?.append) {
          // Append new page's items (mobile infinite scroll). De-dupe by _id
          // in case the same record sneaks in from concurrent requests.
          const existing = new Set(s.items.map((it) => it._id));
          const fresh = a.payload.items.filter(
            (it) => !existing.has(it._id)
          );
          s.items = [...s.items, ...fresh];
        } else {
          s.items = a.payload.items;
        }
        s.pagination = a.payload.pagination;
      })
      .addCase(fetchAuditLogs.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload;
      })
      .addCase(fetchAuditLog.pending, (s) => {
        s.loadingCurrent = true;
      })
      .addCase(fetchAuditLog.fulfilled, (s, a) => {
        s.loadingCurrent = false;
        s.current = a.payload;
      })
      .addCase(fetchAuditLog.rejected, (s) => {
        s.loadingCurrent = false;
      })
      .addCase(fetchAuditFacets.fulfilled, (s, a) => {
        s.facets = a.payload;
      });
  },
});

export const { setQuery, clearCurrent } = slice.actions;
export default slice.reducer;
